/**
 * Sub-dialog for choosing which schemas to include in the blueprint.
 * Wraps the existing `SchemaCheckboxList` and exposes the
 * "Show system schemas" toggle that previously sat next to the inline list.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { SchemaCheckboxList } from './SchemaCheckboxList';

type Props = {
  open: boolean;
  onClose: () => void;
  availableSchemas: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  loading?: boolean;
  includeSystemSchemas: boolean;
  onIncludeSystemSchemasChange: (next: boolean) => void;
};

export function SchemasPickerDialog({
  open,
  onClose,
  availableSchemas,
  selected,
  onChange,
  loading,
  includeSystemSchemas,
  onIncludeSystemSchemasChange,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-xl"
        data-testid="database-blueprint-schemas-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Included schemas</DialogTitle>
          <DialogDescription>
            Pick which schemas this blueprint tracks. System schemas are hidden by
            default — toggle to include them.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="database-blueprint-include-system"
              checked={includeSystemSchemas}
              onCheckedChange={(v) => onIncludeSystemSchemasChange(v === true)}
              data-testid="database-blueprint-include-system"
            />
            <Label htmlFor="database-blueprint-include-system" className="text-sm">
              Show system schemas
            </Label>
          </div>
          <SchemaCheckboxList
            availableSchemas={availableSchemas}
            selected={selected}
            onChange={onChange}
            loading={loading}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            onClick={onClose}
            data-testid="database-blueprint-schemas-done"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
